import { useMap, useToast } from "@ligo/layout-design-core";
import { Errors, Telemetry } from "classes";
import { sortByKey, ss } from "consts";
import {
  AttendanceModel,
  BaseProviderType,
  PermissionProviderType,
  ServicePathEnum,
  SortByEnum,
} from "models";
import { useProfile, useTelemetry } from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as AttendanceService from "services/attendance";
import {
  audioNotification,
  aytyFormatError,
  milisecondsToTimeString,
  notification,
} from "utils";

const AudioPath = require("assets/audios/notification.mp3");

type AttendanceMap = Map<number, AttendanceModel>;

type AttendListContextType = {
  loading: boolean;
  averageTime: number;
  attendances?: AttendanceModel[];
  newAttendances?: AttendanceModel[];
  hasAttendances: boolean;
  queueAmount?: number;
  sortBy?: SortByEnum;
  getAttendances: (isReload?: boolean) => void;
  getAttendance: (id: number) => AttendanceModel | undefined;
  removeAttendance: (key: number) => Promise<AttendanceMap | undefined>;
  verifyFinishAuto: () => void;
  pauseListing: (value: boolean) => void;
  changeSort: (sort: SortByEnum) => void;
};

const AttendListContext = createContext({} as AttendListContextType);

const { lastAttendannce, lastMessage } = SortByEnum;

const initialSort = ss.getItem(sortByKey) as SortByEnum | null;

const maxTimes = 5;

const Provider = ({ children }: BaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [inPause, setInPause] = useState(false);
  const [listingTimes, setListingTimes] = useState<Array<number>>([]);
  const [queueAmount, setQueueAmount] = useState<number>();
  const [list, { set, remove }] = useMap<number, AttendanceModel>();
  const [newAttendances, setNewAttendances] = useState<Array<AttendanceModel>>(
    [],
  );
  const [sortBy, setSortBy] = useState<SortByEnum>(
    initialSort ?? SortByEnum.lastMessage,
  );
  const { pIdProject, user } = useProfile();
  const { addTelemetry } = useTelemetry();
  const { error, warning, info } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const hasAttendances = useMemo(
    () => !!user?.hasAccessAttend && !!list?.size,
    [user, list],
  );

  const attendances = useMemo(() => {
    const isDesc = [lastAttendannce, lastMessage].includes(sortBy);

    if (list)
      return Array.from(list.values()).sort((a, b) => {
        let result = 0;

        let currentA: Date;
        let currentB: Date;

        switch (sortBy) {
          case SortByEnum.lastAttendannce:
          case SortByEnum.oldAttendance:
            currentA = new Date(a.DtStart);
            currentB = new Date(b.DtStart);
            break;
          case SortByEnum.lastMessage:
          case SortByEnum.oldMessage:
            currentA = new Date(a.DtLastMessage || a.DtStart);
            currentB = new Date(b.DtLastMessage || b.DtStart);
            break;
        }

        if (currentA > currentB) result = 1;
        else if (currentA < currentB) result = -1;

        return isDesc ? result * -1 : result;
      });
  }, [list, sortBy]);

  const averageTime = useMemo(() => {
    const sum = listingTimes.reduce((total, value) => total + value, 0);
    return sum / listingTimes.length || 0;
  }, [listingTimes]);

  const getAttendances = useCallback(
    (isReload = false) => {
      if (!inPause) {
        const telemetry = new Telemetry(ServicePathEnum.getAttendances);

        try {
          if (pIdProject) {
            const reqDate = Date.now();

            const result = AttendanceService.getAttendancesSync({ pIdProject });

            const resDate = Date.now();

            setListingTimes((current) => [
              resDate - reqDate,
              ...current.slice(0, maxTimes - 1),
            ]);

            if (result.idReturnAPI > 0) {
              const newAtt = new Array<AttendanceModel>();

              set((current) => {
                const currentMap = new Map(
                  result.omniOnLineList?.map((o) => {
                    if (current && !current.has(o.IdOmni)) newAtt.push(o);

                    return [o.IdOmni, o];
                  }),
                );

                setNewAttendances(newAtt);

                if (!isReload && newAtt.length > 0) {
                  audioNotification(AudioPath);
                  notification(
                    t("newAttendance.title"),
                    t("newAttendance.description"),
                  );

                  newAtt.forEach(
                    ({ DeMessageToDisplay }) =>
                      DeMessageToDisplay &&
                      info({
                        fixed: true,
                        title: t("newAttendance.title"),
                        description: DeMessageToDisplay,
                      }),
                  );
                }

                return currentMap;
              });
            } else warning({ description: t(aytyFormatError(result)) });

            setQueueAmount(result.qtUserOmniRealtimeList);

            addTelemetry(telemetry.finish(), result, {
              qtMSecDurationGetOmniListOnLine: milisecondsToTimeString(
                result.qtMSecDurationGetOmniListOnLine,
              ),
              qtMSecDurationVerifyIfHasMailingAPIDialerAPI:
                milisecondsToTimeString(
                  result.qtMSecDurationVerifyIfHasMailingAPIDialerAPI,
                ),
              idReturnAPIMailingAPI: result.idReturnAPIMailingAPI,
            });
          }
        } catch (e: unknown) {
          if (e instanceof Error) {
            errorsResolver.defaultSyncError(e);
            addTelemetry(telemetry.finish(), e);
          } else console.log("GetAttendances", e);
        } finally {
          setLoading(false);
        }
      }
    },
    [inPause, pIdProject, errorsResolver, set, info, warning, t, addTelemetry],
  );

  const verifyFinishAuto = useCallback(() => {
    if (!inPause) {
      const telemetry = new Telemetry(ServicePathEnum.verifyFinishAuto);

      try {
        if (pIdProject) {
          const result = AttendanceService.verifyFinishAutoSync({
            pIdProject,
          });

          if (result.idReturnAPI <= 0)
            warning({ description: t(aytyFormatError(result)) });

          addTelemetry(telemetry.finish(), result);
        }
      } catch (e: unknown) {
        if (e instanceof Error) {
          errorsResolver.defaultSyncError(e);
          addTelemetry(telemetry.finish(), e);
        } else console.log("verifyFinishAuto", e);
      }
    }
  }, [inPause, pIdProject, errorsResolver, warning, t, addTelemetry]);

  const pauseListing = useCallback((value: boolean) => setInPause(value), []);

  const changeSort = useCallback((sort: SortByEnum) => {
    setSortBy(sort);
    ss.setItem(sortByKey, sort);
  }, []);

  const getAttendance = useCallback((id: number) => list?.get(id), [list]);

  return (
    <AttendListContext.Provider
      value={{
        loading,
        averageTime,
        attendances,
        newAttendances,
        hasAttendances,
        queueAmount,
        sortBy,
        getAttendances,
        getAttendance,
        removeAttendance: remove,
        verifyFinishAuto,
        pauseListing,
        changeSort,
      }}
    >
      {children}
    </AttendListContext.Provider>
  );
};

export const AttendListProvider = ({
  hasPermission = true,
  ...args
}: PermissionProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useAttendList = () => useContext(AttendListContext);
